import { Injectable } from '@angular/core';
import { AppState } from '@api-ui-app/src/app/app.state';
import * as eCommerceProductWizardActions from '@api-ui-app/src/app/ngrx/e-commerce-product-wizard.actions';
import { ProductPreviewScenesService } from '@gelato-api-ui/core/preflight/services/product-preview-scenes.service';
import { getProductUidsFromSanityProduct } from '@gelato-api-ui/core/sanity/helpers/getProductUidsFromSanityProduct';
import { SanityProductCategoryName } from '@gelato-api-ui/core/sanity/constants/sanity-categories/sanity-product-category-name.enum';
import { SanityRootProductCategory } from '@gelato-api-ui/core/sanity/sanity-root-product-category';
import { FeatureSwitcherService } from '@gelato-api-ui/sdk/src/lib/feature-switcher/feature-switcher.service';
import { FeatureFlagEnum } from '@gelato-api-ui/sdk/src/lib/feature-switcher/featureFlagEnum';
import { Store } from '@ngrx/store';
import {
  getSelectedProductCategoryFromSanityContent,
  getSelectedProductFromSanityContent,
  getSelectedRootProductCategoryFromSanityContent,
} from '@product-catalogue/src/lib/ngrx/product-catalogue.reducer';
import { combineLatest } from 'rxjs';
import { map, switchMap, take, tap } from 'rxjs/operators';
import {
  DYNAMIC_APPAREL_SANITY_ROOT_PRODUCT_CATEGORIES,
  DYNAMIC_WALLART_SANITY_ROOT_PRODUCT_CATEGORIES,
} from '../../constants/dynamic-mockups-sanity-product-categories';

@Injectable({ providedIn: 'root' })
export class ECommerceProductWizardMockupService {
  sanityProduct$ = this.store.select(getSelectedProductFromSanityContent);
  sanityCategory$ = this.store.select(getSelectedProductCategoryFromSanityContent);
  sanityRootCategory$ = this.store.select(getSelectedRootProductCategoryFromSanityContent);
  readonly areMockupsEnabledForApparel$ = this.featureSwitcherService.isFeatureEnabledForCurrentUser(
    FeatureFlagEnum.apparel_dynamic_mockups,
  );

  isDynamicMockupsEnabled$ = combineLatest([
    this.sanityRootCategory$,
    this.sanityCategory$,
    this.featureSwitcherService.isFeatureEnabledForCurrentUser(FeatureFlagEnum.mockup_studio),
    this.areMockupsEnabledForApparel$,
  ]).pipe(
    map(([rootCategory, category, enabled, apparelDynamicMockupsEnabled]) => {
      const enabledCategoriesForApparel = apparelDynamicMockupsEnabled
        ? DYNAMIC_APPAREL_SANITY_ROOT_PRODUCT_CATEGORIES
        : [];

      return (
        [...DYNAMIC_WALLART_SANITY_ROOT_PRODUCT_CATEGORIES, ...enabledCategoriesForApparel].includes(
          rootCategory?.productModel as SanityRootProductCategory,
        ) &&
        ![SanityProductCategoryName.WALLPAPER].includes(category?.productModel as SanityProductCategoryName) &&
        enabled
      );
    }),
  );

  constructor(
    private readonly store: Store<AppState>,
    private readonly productPreviewScenesService: ProductPreviewScenesService,
    private readonly featureSwitcherService: FeatureSwitcherService,
  ) {}

  loadSupportedPreviewScenes(productUid: string = null, enableDynamicMockups: boolean = false, clientId?: string) {
    this.sanityProduct$
      .pipe(
        take(1),
        switchMap(sanityProduct => {
          const productUids = productUid ? [productUid] : getProductUidsFromSanityProduct(sanityProduct);
          return this.productPreviewScenesService.get(productUids[0], false, enableDynamicMockups, clientId);
        }),
        tap(supportedPreviewScenes => {
          this.store.dispatch(eCommerceProductWizardActions.setSupportedPreviewScenes({ supportedPreviewScenes }));
        }),
      )
      .subscribe();
  }
}
